import React from 'react';
import { Flame, Gear as Cog, GasPump as Fuel, Gauge } from '@phosphor-icons/react';
import { parseEngineCC } from '../../utils/engine';
import { useOptimizedImageStatic } from '../../hooks/useOptimizedImage';
import { useDutyFree } from '../../context/DutyFreeContext';
import { useTranslation } from '../../i18n/I18nContext';
import { getDisplayImages } from '../../utils/carImage';
import { useTranslateVehicleValue } from '../../utils/vehicleValues';
import CarImagePlaceholder from '../CarImagePlaceholder';
import { CardRibbon } from '../ui/card-ribbon';
import omodaLogo from '../../assets/dealers/omoda-logo.png';
import jaecooLogo from '../../assets/dealers/jaecoo-logo.png';
import './SalonCarCard.css';

const BRAND_BADGES = {
  omoda: { src: omodaLogo, alt: 'OMODA' },
  jaecoo: { src: jaecooLogo, alt: 'JAECOO' },
};

/** Резолвит brand-badge по car.make (case-insensitive). Возвращает null
 *  если бренд не OMODA/JAECOO (например для used-VIN cars в других flow). */
const resolveBrandBadge = (make) => {
  if (!make) return null;
  const key = make.toLowerCase().replace(/\*/g, '').trim();
  if (key.includes('omoda')) return BRAND_BADGES.omoda;
  if (key.includes('jaecoo')) return BRAND_BADGES.jaecoo;
  return null;
};

/**
 * SalonCarCard — slim card-вариант для салонного Welcome flow (issue #1022).
 * Узкий feature set:
 *   - крупное фото вверху (top 60% карточки),
 *   - название модели,
 *   - 3 спеки: engine displacement / transmission / fuelType — БЕЗ года и пробега
 *     (Маша 2026-06-22: «уберём year + mileage, добавим больше места под фото»),
 *   - цена.
 *
 * Не использует общий `CarCard` чтобы не раздувать его prop-чеклистом
 * («hideYear», «hideMileage», «variantSalon»). Salon-flow — самостоятельная
 * композиция; carousel + dealer preset делают остальную работу.
 *
 * @param {object} props
 * @param {object} props.car — DealerCarDto.
 * @param {(car: object) => void} [props.onClick]
 * @param {boolean} [props.showStatus=false] — метки «промо» / «зарезервировано»
 *   на фото (атом `CardRibbon`, как на карточке каталога). По умолчанию
 *   выключены — Welcome Autodis их не показывает (#1456).
 */
export const SalonCarCard = ({ car, onClick, showStatus = false }) => {
  const { isDutyFree, calculateDutyFree } = useDutyFree();
  const { t } = useTranslation();
  // Тот же переводчик значений, что у лендинга и каталога (#1456): на
  // витрине — словарь инвентаря на языке покупателя («Manual» → «Manuelle»),
  // у дилеров — прежний словарь листовок, как раньше. Голый словарь листовок
  // на французском возвращал сырое значение стока, и витрина показывала
  // «Manual», «Petrol».
  const translateVehicleValue = useTranslateVehicleValue();
  const displayImages = React.useMemo(() => getDisplayImages(car), [car]);
  const firstImage = displayImages[0] || null;
  // Только превью: оригинал — если ресайз не удался, и не «пока ждём» (#1469).
  const { src: displaySrc, pending: photoPending } = useOptimizedImageStatic(firstImage, 650, 480, {
    lazy: false,
  });

  const engineCC = parseEngineCC(car.engineDisplacement);
  const cleanMake = car.make?.replace(/\*/g, '') || car.make;
  const cleanModel = car.model?.replace(/\*/g, '') || car.model;
  const displayTitle = car.name || `${cleanMake ?? ''} ${cleanModel ?? ''}`.trim();
  const displayPrice = isDutyFree ? calculateDutyFree(car) : car.price;
  const brandBadge = resolveBrandBadge(car.make);
  // Метки «промо» / «зарезервировано» — те же, что на карточке каталога
  // (`CarCard`), и только по запросу (`showStatus`): каталог витрины по QR
  // (#1456) без них показывал зарезервированную машину как свободную. У
  // Autodis prop не передаётся — их карточки остаются без меток, как были.
  const isSold = car.statusSold?.toLowerCase() === 'yes';
  const hasStatus = showStatus && (car.isHotOffer || isSold);

  return (
    <button
      type="button"
      className="salon-car-card"
      onClick={() => onClick?.(car)}
    >
      <div className="salon-car-card__photo">
        {hasStatus && (
          <div className="salon-car-card__ribbons">
            {car.isHotOffer && (
              <CardRibbon variant="hot">
                <Flame size={12} weight="bold" fill="currentColor" />
                {t('landing.card.hot', 'HOT')}
              </CardRibbon>
            )}
            {isSold && (
              <CardRibbon variant="sold">
                {t('landing.card.reserved', 'RESERVED')}
              </CardRibbon>
            )}
          </div>
        )}
        {displaySrc ? (
          <img
            src={displaySrc}
            alt={displayTitle}
            className="salon-car-card__img"
            loading="lazy"
            /* Per-car crop через `imagePosition` (не-каноническое поле,
               едет из каталога b2b-api через attributes open-bag) — когда
               landscape hero не центрируется хорошо по default, data-team
               задаёт оффсет (например "20% 30%" для JAECOO 8). Тот же
               механизм уже используется в HeroPhoto/LandingBody. */
            style={car.imagePosition ? { objectPosition: car.imagePosition } : undefined}
          />
        ) : photoPending ? null : (
          <CarImagePlaceholder />
        )}
      </div>
      {/* Badge — sibling photo, не child. Photo имеет overflow:hidden
          (для clipping картинки), поэтому badge внутри photo обрезался
          бы по верхнему краю. Здесь же badge anchor против карточки
          (которая overflow:visible) и парит над её верхней границей. */}
      {brandBadge && (
        <span
          className="salon-car-card__brand-badge"
          aria-label={brandBadge.alt}
        >
          <img
            src={brandBadge.src}
            alt=""
            className="salon-car-card__brand-badge-logo"
          />
        </span>
      )}

      <div className="salon-car-card__body">
        <h3 className="salon-car-card__title">{displayTitle}</h3>

        <div className="salon-car-card__specs" aria-label={t('landing.card.specifications', 'Specifications')}>
          {engineCC && (
            <span className="salon-car-card__spec">
              <Gauge size={16} weight="regular" aria-hidden="true" />
              {engineCC}cc
            </span>
          )}
          {car.transmission && (
            <span className="salon-car-card__spec">
              <Cog size={16} weight="regular" aria-hidden="true" />
              {translateVehicleValue(car.transmission, 'transmission')}
            </span>
          )}
          {car.fuelType && (
            <span className="salon-car-card__spec">
              <Fuel size={16} weight="regular" aria-hidden="true" />
              {translateVehicleValue(car.fuelType, 'fuelType')}
            </span>
          )}
        </div>

        <div className="salon-car-card__price">
          {displayPrice > 0
            ? `€${displayPrice.toLocaleString()}`
            : t('landing.card.contactForPrice', 'Contact for price')}
        </div>
      </div>
    </button>
  );
};

export default SalonCarCard;
